import { css } from '@emotion/css';

export const anchorNone = css`
  overflow-anchor: none;
`;

export const anchorAuto = css`
  overflow-anchor: auto;
  height: 10px;
`;

export const scrollContainerHeight = css`
  height: calc(100vh - 18rem);
  max-height: calc(100vh - 18rem);
`;

export const overflowOverlay = css`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: 8rem;
  background: linear-gradient(360deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%);
`;

export const markupContainerStyles = css`
  a {
    word-break: break-all;
    color: var(--link-01);
  }

  pre {
    white-space: pre-wrap;
  }
`;

export const inlineP = css`
  & > p {
    display: inline;
    margin-inline-end: 4px;
  }
`;

export const mentionHelperStyles = css`
  .mention {
    color: #0065ff;
    background-color: transparent;
    border-radius: 4px;
    cursor: pointer;
    padding: 0px;
    &:hover {
      background-color: #dbeafe;
    }
  }
`;

/**
 * Styling for rich Coach answers (code, tables, callouts, math). The same HTML is
 * produced two ways — live by the editor markdown parser while streaming, and by
 * the backend markdown-it converter once persisted — so these rules deliberately
 * target both shapes (e.g. `.code-block pre` for the live stream and `pre code` for
 * the persisted answer) to guarantee stream === refresh. The Prism token colours
 * come from the global `prism.css`; KaTeX styling from `katex.min.css`.
 */
export const aiAnswerStyles = css`
  /* Inline code — persisted (\`<code>\`) and live (\`<code class="code-01">\`). */
  :not(pre) > code,
  code.code-01 {
    font-family: Consolas, Monaco, 'Andale Mono', 'Ubuntu Mono', monospace;
    font-size: 0.85em;
    background: var(--bg-secondary, #f1f5f9);
    color: var(--text-text-primary, #0f172a);
    padding: 0.15em 0.4em;
    border-radius: 4px;
    white-space: break-spaces;
    word-break: break-word;
  }

  /* Code blocks — persisted (\`pre > code\`) and live (\`.code-block > pre > code\`). */
  pre {
    background: #e3eaf2;
    border-radius: 8px;
    padding: 1.2rem 1.4rem;
    margin: 1.2rem 0;
    overflow-x: auto;
    white-space: pre;
  }

  pre code {
    background: transparent;
    padding: 0;
    border: 0;
    font-size: 0.85em;
    color: inherit;
    white-space: inherit;
    word-break: normal;
  }

  .code-block {
    margin: 1.2rem 0;
  }

  .code-block pre {
    margin: 0;
  }

  /* Tables */
  table {
    border-collapse: collapse;
    width: 100%;
    margin: 1.2rem 0;
    font-size: 0.92em;
    display: block;
    overflow-x: auto;
  }

  th,
  td {
    border: 1px solid var(--border, #e2e8f0);
    padding: 0.5rem 0.8rem;
    text-align: start;
  }

  th {
    background: var(--bg-secondary, #f8fafc);
    font-weight: 600;
  }

  /* Callouts (\`:::note/tip/info/warning\`) */
  .callout {
    border-radius: 8px;
    padding: 1rem 1.2rem;
    margin: 1.2rem 0;
    border-inline-start: 4px solid var(--callout-accent, #3b82f6);
    background: var(--callout-bg, #eff6ff);
  }

  .callout > *:first-child {
    margin-top: 0;
  }

  .callout > *:last-child {
    margin-bottom: 0;
  }

  .callout-note {
    --callout-accent: #3b82f6;
    --callout-bg: #eff6ff;
  }

  .callout-tip {
    --callout-accent: #10b981;
    --callout-bg: #ecfdf5;
  }

  .callout-info {
    --callout-accent: #6366f1;
    --callout-bg: #eef2ff;
  }

  .callout-warning {
    --callout-accent: #f59e0b;
    --callout-bg: #fffbeb;
  }

  blockquote {
    border-inline-start: 3px solid var(--border-dark, #cbd5e1);
    padding-inline-start: 1rem;
    color: var(--text-text-secondary, #64748b);
    margin: 1rem 0;
  }

  /* KaTeX */
  .katex-display {
    overflow-x: auto;
    overflow-y: hidden;
    padding: 0.4rem 0;
  }
`;

export const markupHelperStyles = css`
  h1,
  h2,
  h3 {
    font-weight: 600;
    &:not(:first-child) {
      margin-top: 30px;
    }
  }

  ul,
  ol {
    li {
      margin-inline-start: 2rem;
      margin-top: 0.6rem !important;
      p {
        margin-top: 0 !important;
      }
    }
  }

  ul {
    list-style-type: disc;
  }

  & > *:not(:first-child) {
    margin-top: 0.8rem;
  }
`;

export const reasoningStyles = css`
  h1,
  h2,
  h3 {
    font-weight: 600;
    margin-bottom: 0.2rem;
    font-size: 1.4rem;
  }
  line-height: 2.4rem;
`;

export const reasoningBulletStyles = css`
  .unit {
    position: relative;
    padding: 8px 0;
    margin-left: 16px;
  }

  .unit:not(:last-child):before {
    position: absolute;
    content: '';
    width: 1px;
    background-color: var(--border-dark);
    left: -17px;
    top: 20px;
    bottom: -20px;
    z-index: 1;
  }

  .reasoning-bullet-container {
    position: absolute;
    top: 9.5px;
    left: -19.5px;
    padding: 8px 0;
    background-color: var(--bg-primary);
    z-index: 2;
  }

  .reasoning-bullet {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background-color: var(--border-dark);
    border: 1px solid var(--border-dark);
  }

  .reasoning-bullet-container-done {
    top: 4px;
    left: -24px;
  }
`;

export const shimmerEffect = css`
  color: rgba(255, 255, 255, 0.1);
  background: linear-gradient(to right, #222 0%, #fff 50%, #222 100%);
  background-size: 250px 100%;
  background-clip: text;
  -webkit-background-clip: text; /* Safari */
  color: transparent;
  animation: shimmer 2s infinite;
  background-repeat: no-repeat;
  background-position: 0 0;

  @keyframes shimmer {
    0% {
      background-position: top left;
    }
    65% {
      background-position: top right;
    }
    100% {
      background-position: top left;
    }
  }
`;
